import { isJsonObjectValue, type JsonObject } from "@/types/json"
import type { ScriptTurn } from "@/types/ScriptTurn"

import { ACTION_KINDS, type SofiaActionKind } from "../model/constants"
import type { RenderVisualizationArgs } from "../model/types"
import { parseChartSpec } from "./chartSpec"

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
}

export function isKnownKind(value: unknown): value is SofiaActionKind {
  return (
    typeof value === "string" &&
    (ACTION_KINDS as readonly string[]).includes(value)
  )
}

export function readDraftTestCases(
  args: Record<string, unknown>,
  problems?: string[],
): Array<{
  input_str: string
  description?: string
  expected_behaviors?: string[]
  expected_output_json?: JsonObject
  input_type?: string
  script_turns?: ScriptTurn[]
}> | null {
  const value = args.test_cases
  if (!Array.isArray(value) || value.length === 0 || value.length > 50) {
    return null
  }
  const cases = []
  for (const [index, entry] of value.entries()) {
    if (!isRecord(entry)) {
      return null
    }
    const input_str = readString(entry, "input_str")
    if (!input_str) {
      return null
    }
    const expected_output_json = readJsonObject(entry, "expected_output_json")
    if ("expected_output_json" in entry && expected_output_json === undefined) {
      return null
    }
    const scriptProblems = problems ?? []
    const before = scriptProblems.length
    const { input_type, script_turns } = readScriptFields(
      entry,
      scriptProblems,
      `test_cases[${index}].`,
    )
    if (scriptProblems.length > before) {
      return null
    }
    cases.push({
      input_str,
      description: readString(entry, "description"),
      expected_behaviors: readStringArray(entry, "expected_behaviors"),
      expected_output_json,
      input_type,
      script_turns,
    })
  }
  return cases
}

export function readString(
  args: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = args[key]
  return typeof value === "string" && value.trim().length > 0
    ? value.trim()
    : undefined
}

export function readStringAllowEmpty(
  args: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = args[key]
  return typeof value === "string" ? value.trim() : undefined
}

export function readStringArray(
  args: Record<string, unknown>,
  key: string,
): string[] | undefined {
  const value = args[key]
  if (!Array.isArray(value)) {
    return undefined
  }
  if (!value.every((item): item is string => typeof item === "string")) {
    return undefined
  }
  const strings = value
    .map((item) => item.trim())
    .filter((item) => item.length > 0)
  return strings.length === value.length ? strings : undefined
}

/**
 * Ceiling on a multi-batch draft request — lockstep with the max_value on
 * sofia's draft_test_cases requested_total arg (product/action_registry.py).
 */
export const MAX_REQUESTED_TEST_CASE_TOTAL = 500

/**
 * The full count the user asked for when it exceeds one batch. Returns
 * undefined when absent, and when present but malformed pushes the matching
 * problem (non-integer, above the ceiling, or below this batch's own count —
 * distinct reasons, mirroring sofia's registry bound checks in
 * action_spec.py and its batch-coverage post-validation) and returns
 * undefined. The value otherwise.
 */
export function readRequestedTotal(
  args: Record<string, unknown>,
  batchCount: number,
  problems: string[],
): number | undefined {
  if (!("requested_total" in args)) {
    return undefined
  }
  const value = readNumber(args, "requested_total")
  if (value === undefined || !Number.isInteger(value)) {
    problems.push("requested_total must be an integer")
    return undefined
  }
  if (value > MAX_REQUESTED_TEST_CASE_TOTAL) {
    problems.push(
      `requested_total must be at most ${MAX_REQUESTED_TEST_CASE_TOTAL}`,
    )
    return undefined
  }
  if (value < batchCount) {
    problems.push(
      `requested_total must cover at least this batch's ${batchCount} test case${batchCount === 1 ? "" : "s"}`,
    )
    return undefined
  }
  return value
}

export function readNumber(
  args: Record<string, unknown>,
  key: string,
): number | undefined {
  const value = args[key]
  return typeof value === "number" && Number.isFinite(value) ? value : undefined
}

export function readBoolean(
  args: Record<string, unknown>,
  key: string,
): boolean | undefined {
  const value = args[key]
  return typeof value === "boolean" ? value : undefined
}

export function readJsonObject(
  args: Record<string, unknown>,
  key: string,
): JsonObject | undefined {
  const value = args[key]
  return isJsonObjectValue(value) ? value : undefined
}

const TEST_CASE_INPUT_TYPES = ["SCENARIO", "SCRIPT"] as const

// Mirrors the /v1 ScriptTurnDtmfDict wire pattern, which is looser than the
// editor's strict keypad charset; at least one real keypad character is
// still required so separator-only strings don't press nothing.
const DTMF_TURN_DIGITS_REGEX = /^[0-9*#+(). -]+$/
const DTMF_KEYPAD_CHAR_REGEX = /[0-9*#]/

export function readInputType(
  args: Record<string, unknown>,
  problems: string[],
  prefix = "",
): "SCENARIO" | "SCRIPT" | undefined {
  if (args.input_type === undefined) {
    return undefined
  }
  const value = readStringEnum(args, "input_type", TEST_CASE_INPUT_TYPES)
  if (value === undefined) {
    problems.push(`${prefix}input_type must be "SCENARIO" or "SCRIPT"`)
  }
  return value
}

export function readScriptTurns(
  args: Record<string, unknown>,
  key: string,
  problems: string[],
  prefix = "",
): ScriptTurn[] | undefined {
  const value = args[key]
  if (value === undefined) {
    return undefined
  }
  const label = `${prefix}${key}`
  if (!Array.isArray(value) || value.length === 0) {
    problems.push(`${label} must be a non-empty array of script turns`)
    return undefined
  }
  const turns: ScriptTurn[] = []
  for (const [index, entry] of value.entries()) {
    if (typeof entry === "string") {
      const trimmed = entry.trim()
      if (trimmed.length === 0) {
        problems.push(`${label}[${index}] must be a non-empty string`)
        return undefined
      }
      turns.push(trimmed)
      continue
    }
    if (!isRecord(entry) || typeof entry.type !== "string") {
      problems.push(
        `${label}[${index}] must be a string or a turn dict with type "text" or "dtmf"`,
      )
      return undefined
    }
    if (entry.type === "text") {
      const text = entry.text
      if (typeof text !== "string" || text.trim().length === 0) {
        problems.push(`${label}[${index}] text must be a non-empty string`)
        return undefined
      }
      turns.push({ type: "text", text: text.trim() })
      continue
    }
    if (entry.type === "dtmf") {
      const digits = entry.digits
      if (
        typeof digits !== "string" ||
        !DTMF_TURN_DIGITS_REGEX.test(digits) ||
        !DTMF_KEYPAD_CHAR_REGEX.test(digits)
      ) {
        problems.push(
          `${label}[${index}] has invalid dtmf digits (0-9, *, # and separators only)`,
        )
        return undefined
      }
      turns.push({ type: "dtmf", digits })
      continue
    }
    problems.push(
      `${label}[${index}] has unsupported turn type "${entry.type}"`,
    )
    return undefined
  }
  return turns
}

/**
 * Narrow input_type + script_turns where a FULL test case is built (add/draft),
 * enforcing the backend pairing rule: SCRIPT and non-empty script_turns come
 * together. update_test_case is a partial patch and skips the pairing.
 */
export function readScriptFields(
  entry: Record<string, unknown>,
  problems: string[],
  prefix = "",
): { input_type?: "SCENARIO" | "SCRIPT"; script_turns?: ScriptTurn[] } {
  const input_type = readInputType(entry, problems, prefix)
  const script_turns = readScriptTurns(entry, "script_turns", problems, prefix)
  const turnsPresent = entry.script_turns !== undefined
  if (turnsPresent && input_type !== "SCRIPT") {
    problems.push(`${prefix}script_turns requires input_type "SCRIPT"`)
  }
  if (input_type === "SCRIPT" && !turnsPresent) {
    problems.push(
      `${prefix}input_type "SCRIPT" requires non-empty script_turns`,
    )
  }
  return { input_type, script_turns }
}

export function readStringEnum<const T extends readonly string[]>(
  args: Record<string, unknown>,
  key: string,
  values: T,
): T[number] | undefined {
  const value = readString(args, key)
  return value && (values as readonly string[]).includes(value)
    ? (value as T[number])
    : undefined
}

/**
 * Deep-validate render_visualization args: a non-empty title plus a
 * CovalChartSpec v1 that passes the fail-closed `parseChartSpec` (strict
 * schema, closed color/format enums, row/series/size caps). Returns null on
 * ANY problem — callers drop the proposal, never render a partial chart.
 */
export function parseRenderVisualizationArgs(
  args: Record<string, unknown>,
): RenderVisualizationArgs | null {
  const title = readString(args, "title")
  if (!title) {
    return null
  }
  const spec = parseChartSpec(args.spec)
  if (!spec) {
    return null
  }
  return { title, spec }
}
